import React, { useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Box, PolymorphicComponentProps } from 'react-polymorphic-box';
import { useLocale, useTranslations } from 'next-intl';

const defaultElement = 'div';

type ReadMoreProps<E extends React.ElementType> = PolymorphicComponentProps<
  E,
  {
    mobileLength: number;
    desktopLength: number;
    fullContent?: string | null;
    dotOnly?: boolean;
    content: string;
    readMoreClass?: string;
    readMoreText?: string;
  }
>;

const ReadMore: <E extends React.ElementType = typeof defaultElement>(
  props: ReadMoreProps<E>
) => JSX.Element = (props) => {
  const locale = useLocale();
  const translation = useTranslations('components.ReadMore')
  const { mobileLength, desktopLength, fullContent, dotOnly, content: rawContent, readMoreClass, readMoreText, ...restProps } = props;
  // Some API fields passed as `content` can be null.
  const content = rawContent || '';
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [innerHtml, setInnerHtml] = useState({
    __html: content,
  });
  const [isShorten, setIsShorten] = useState(false);

  const limitChars = isDesktop ? desktopLength : mobileLength;
  const reveal = () => {
    setInnerHtml({ __html: fullContent || content });
    setIsShorten(false);
  };

  useEffect(() => {
    if (content.length < limitChars) {
      if (fullContent) {
        setInnerHtml({ __html: fullContent });
      }
      setIsShorten(false);
      return;
    }

    let shortenContent = '';

    if (locale === 'zh') {
      shortenContent = content.substring(0, limitChars);
    } else {
      const index = content.lastIndexOf(' ', limitChars - 1);
      shortenContent = index > 0 ? content.substring(0, index) : content.substring(0, limitChars);
    }

    setInnerHtml({ __html: shortenContent });
    setIsShorten(true);
  }, [content, limitChars]);

  return (
    <Box as={defaultElement} {...(restProps as any)}>
      <span>
        <span dangerouslySetInnerHTML={innerHtml} />
      </span>
      {isShorten ? (
        <span onClick={() => (dotOnly ? null : reveal())}>
          ...
          {!dotOnly && <span className={readMoreClass || 'tw-ml-1 tw-cursor-pointer tw-text-brand-navy tw-lowercase tw-underline'}>{readMoreText || translation('readMore')}</span>}
        </span>
      ) : (
        ''
      )}
    </Box>
  );
};

export default ReadMore;
